import { useEffect } from 'react'
import * as THREE from 'three'
import { createClippedShapeMaterial, createHolographicMaterial } from './ClippedShapeMaterial'

// Material presets for different city elements and teams
export const MATERIALS = {
  // New York - White (opaque) with polygon offset to prevent z-fighting
  newYork: {
    color: '#ffffff',
    emissive: '#aaaaaa',
    emissiveIntensity: 0.3,
    metalness: 0.1,
    roughness: 0.4,
    envMapIntensity: 1.5,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1
  },
  
  // Tokyo - Dark blue (opaque) with polygon offset to prevent z-fighting
  tokyo: {
    color: '#1a3a6e',
    emissive: '#0a2244',
    emissiveIntensity: 0.5,
    metalness: 0.3,
    roughness: 0.2,
    envMapIntensity: 2.0,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1
  },
  
  // Tokyo Outline Only - Invisible front faces, silhouette shows as white outline
  tokyoOutline: {
    color: '#000000',
    emissive: '#000000',
    emissiveIntensity: 0,
    metalness: 0,
    roughness: 1,
    transparent: true,
    opacity: 0,
    side: THREE.FrontSide,
    depthWrite: false,
    depthTest: false
  },
  
  // Virtual building - Darker UI Blue (matches font color)
  virtualBuilding: {
    color: '#2a7ab8',
    emissive: '#1a4a70',
    emissiveIntensity: 0.4,
    metalness: 0.3,
    roughness: 0.2,
    envMapIntensity: 2.0,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: 2,
    polygonOffsetUnits: 1
  },
  
  // City buildings (PROX) - Super Dark Grey
  cityDark: {
    color: '#1a1a1a',
    emissive: '#0a0a0a',
    emissiveIntensity: 0.1,
    metalness: 0.9,
    roughness: 0.3,
    envMapIntensity: 2.5,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: 2,
    polygonOffsetUnits: 1
  },
  
  // Portal/Virtual stadium - Darker UI Blue (matches font color)
  portal: {
    color: '#2a7ab8',
    emissive: '#1a4a70',
    emissiveIntensity: 0.4,
    metalness: 0.3,
    roughness: 0.2,
    transparent: true,
    opacity: 0.85,
    envMapIntensity: 2.0,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: 2,
    polygonOffsetUnits: 1
  },
  
  // Game glass and roof - Fully transparent, always visible on top
  gameGlass: {
    color: '#ffffff',
    emissive: '#ffffff',
    emissiveIntensity: 0.1,
    metalness: 0.1,
    roughness: 0.1,
    transparent: true,
    opacity: 0.05,
    side: THREE.DoubleSide,
    envMapIntensity: 1.0,
    toneMapped: false,
    depthWrite: false,
    depthTest: false,
    polygonOffset: true,
    polygonOffsetFactor: 3,
    polygonOffsetUnits: 1
  },
  
  // Proximity buildings - Transparent glass
  proximity: {
    color: '#404048',
    emissive: '#303038',
    emissiveIntensity: 0.2,
    metalness: 0.9,
    roughness: 0.2,
    transparent: true,
    opacity: 0.4,
    side: THREE.DoubleSide,
    envMapIntensity: 2.5,
    depthWrite: false,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: 1,
    polygonOffsetUnits: 1
  },
  
  // Stadium - With polygon offset to prevent z-fighting
  stadium: {
    color: '#6a6a70',
    emissive: '#4a4a50',
    emissiveIntensity: 0.4,
    metalness: 0.6,
    roughness: 0.35,
    envMapIntensity: 2.0,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: 2,
    polygonOffsetUnits: 1
  },
  
  // Team A (NY side) - SOLID material with subtle depth
  teamA: {
    color: '#4ecdc4',  // Teal/cyan like reference
    emissive: '#1a4a47',  // Subtle dark teal self-illumination
    emissiveIntensity: 0.2,
    metalness: 0.4,
    roughness: 0.4,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    clipShadows: true,
    clipIntersection: false
  },
  
  // Team B (Tokyo side) - SOLID material with subtle depth
  teamB: {
    color: '#9b59b6',  // Purple like reference
    emissive: '#3d2347',  // Subtle dark purple self-illumination
    emissiveIntensity: 0.2,
    metalness: 0.4,
    roughness: 0.4,
    side: THREE.DoubleSide,
    depthWrite: true,
    depthTest: true,
    clipShadows: true,
    clipIntersection: false
  },
  
  
  // Drones and tech - Subtle graphite glow, mostly transparent
  tech: {
    color: '#1b1416',
    emissive: '#471a1f',
    emissiveIntensity: 0.2,
    metalness: 0.78,
    roughness: 0.3,
    transparent: true,
    opacity: 0.25,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: true
  },
  
  // Gradient hide - Custom gradient effect (handled separately)
  gradientHide: {
    color: '#0a0a0a',
    emissive: '#050505',
    emissiveIntensity: 0.1,
    metalness: 0.5,
    roughness: 0.6,
    transparent: true,
    opacity: 0.4,
    side: THREE.DoubleSide,
    depthWrite: false,
    depthTest: true
  }
}

export function applyMaterialToModel(object, materialConfig, clippingPlanes = null) {
  object.traverse((child) => {
    if (child.isMesh) {
      // For team shapes with clipping, use SOLID clipping material
      // Cross-sections are computed geometrically by CrossSectionComputer
      if (clippingPlanes) {
        // Use the color from materialConfig (which may be overridden by teamColor)
        child.material = createClippedShapeMaterial(materialConfig.color, clippingPlanes)
        child.renderOrder = 10000
      } else {
        const variance = Math.random() * 0.1 - 0.05
        const baseColor = new THREE.Color(materialConfig.color)
        const variedColor = baseColor.clone().offsetHSL(0, 0, variance)
        
        const mat = new THREE.MeshStandardMaterial({
          ...materialConfig,
          color: variedColor,
          clippingPlanes: clippingPlanes,
          clipShadows: clippingPlanes ? true : false
        })
        
        if (materialConfig === MATERIALS.gameGlass) {
          mat.depthTest = false
          child.renderOrder = 9999
        }
        
        child.material = mat
      }
      child.castShadow = true
      child.receiveShadow = true
    }
  })
}

export function applyGradientMaterial(object) {
  object.traverse((child) => {
    if (child.isMesh) {
      child.renderOrder = 9500
      
      const material = new THREE.ShaderMaterial({
        transparent: true,
        side: THREE.DoubleSide,
        depthWrite: false,
        depthTest: false,
        blending: THREE.AdditiveBlending,
        toneMapped: false,
        uniforms: {
          colorBottom: { value: new THREE.Color('#66aaff') }, // Brighter blue
          colorMid: { value: new THREE.Color('#4488ff') },    // Intense blue
          colorTop: { value: new THREE.Color('#1155aa') },    // Medium blue (not too dark)
          opacityBottom: { value: 1.0 },                      // Full opacity at bottom
          opacityTop: { value: 0.15 },                        // More visible at top
          emissiveIntensity: { value: 2.5 },                  // Much brighter glow
          time: { value: 0 }
        },
        vertexShader: `
          varying vec3 vPosition;
          varying vec3 vNormal;
          varying vec3 vWorldPosition;
          
          void main() {
            vPosition = position;
            vNormal = normal;
            vec4 worldPos = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPos.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: `
          uniform vec3 colorBottom;
          uniform vec3 colorMid;
          uniform vec3 colorTop;
          uniform float opacityBottom;
          uniform float opacityTop;
          uniform float emissiveIntensity;
          uniform float time;
          
          varying vec3 vPosition;
          varying vec3 vNormal;
          varying vec3 vWorldPosition;
          
          float smoothNoise(vec3 pos, float scale) {
            float noise1 = sin(pos.x * 0.1 * scale + time) * sin(pos.z * 0.1 * scale + time * 0.7) * 0.5 + 0.5;
            float noise2 = sin(pos.x * 0.2 * scale + time * 0.8) * sin(pos.z * 0.2 * scale + time * 1.1) * 0.5 + 0.5;
            float noise3 = sin(pos.x * 0.07 * scale + time * 1.2) * sin(pos.z * 0.07 * scale + time * 0.9) * 0.5 + 0.5;
            return (noise1 * 0.5 + noise2 * 0.3 + noise3 * 0.2);
          }
          
          void main() {
            float normalizedY = (vPosition.y + 50.0) / 100.0;
            normalizedY = clamp(normalizedY, 0.0, 1.0);
            
            vec3 color;
            if (normalizedY < 0.4) {
              color = mix(colorBottom, colorMid, normalizedY * 1.5);
            } else {
              color = mix(colorMid, colorTop, (normalizedY - 0.4) * 1.67);
            }
            
            float baseHeight = 0.45;  // Much higher - gradient extends much further up
            float opacity;
            
            if (normalizedY < baseHeight) {
              opacity = mix(opacityBottom, opacityBottom * 0.95, normalizedY / baseHeight);
            } else {
              float t = (normalizedY - baseHeight) / (1.0 - baseHeight);
              t = t * t * (3.0 - 2.0 * t);
              t = pow(t, 1.2);  // Even slower falloff - stays visible much longer
              opacity = mix(opacityBottom * 0.95, opacityTop, t);
            }
            
            float spatialNoise = smoothNoise(vWorldPosition, 0.5);
            spatialNoise = spatialNoise * 0.22 + 0.83;
            
            float timeEffect = sin(time * 0.5 + vPosition.y * 0.05) * 0.12 + 0.88;
            opacity *= timeEffect * spatialNoise;
            
            vec3 viewDirection = normalize(cameraPosition - vWorldPosition);
            float fresnel = pow(1.0 - abs(dot(viewDirection, vNormal)), 2.6);
            
            float horizontalDist = length(vPosition.xz);
            float borderEffect = smoothstep(2.0, 10.0, horizontalDist) * 1.35;
            
            vec3 emissive = color * emissiveIntensity * (1.0 + fresnel * 0.95 + borderEffect);
            
            float variationAmt = 0.13;
            emissive *= (1.0 - variationAmt + variationAmt * spatialNoise);
            
            if (normalizedY > baseHeight) {
              float heightFade = pow(1.0 - (normalizedY - baseHeight) / (1.0 - baseHeight), 1.8);
              opacity *= heightFade * 0.95 + 0.1;
            }
            
            opacity *= 1.0;  // No reduction - full visibility
            
            float transparencyNoise = smoothNoise(vWorldPosition * 2.0, 0.8);
            if (transparencyNoise < 0.15) {
              opacity *= transparencyNoise * 4.0 + 0.4;  // Less aggressive transparency cutoff
            }
            
            gl_FragColor = vec4(emissive, opacity);
          }
        `
      });
      
      child.material = material;
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });
}

export function useCyberpunkMaterial(object, materialType) {
  useEffect(() => {
    if (object && MATERIALS[materialType]) {
      applyMaterialToModel(object, MATERIALS[materialType])
    }
  }, [object, materialType])
}

